
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Student } from '@/services/db/types';
import { getStudentById, deleteStudent } from '@/services/studentService';
import { toast } from 'sonner';
import LoadingState from './LoadingState';
import ErrorState from './ErrorState';
import StudentHeader from './StudentHeader';
import StudentSummaryCard from './StudentSummaryCard';
import ProgramsCard from './ProgramsCard';
import StudentPaymentSection from './StudentPaymentSection';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ExamsTab from '../student-exams/ExamsTab';
import CertificationsTab from '../student-certification/CertificationsTab';
import EnrollmentHistoryTab from './EnrollmentHistoryTab';
import { BookOpen, FileText, Badge, History } from 'lucide-react';

export const StudentDetailsContent = ({
  studentId
}: {
  studentId: string;
}) => {
  const [student, setStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');
  const navigate = useNavigate();

  const calculateTotalFees = useCallback((): number => {
    if (!student || !student.programs || !Array.isArray(student.programs)) return 0;
    return student.programs.reduce((total, program) => {
      const fee = typeof program.fee === 'number' ? program.fee : parseFloat(program.fee as any || '0');
      return total + (isNaN(fee) ? 0 : fee);
    }, 0);
  }, [student]);

  const fetchId = useCallback(async (id: string) => {
    if (!id) {
      setError('Student ID is missing');
      setLoading(false);
      return;
    }
    try {
      console.log(`🔍 Fetching student with ID: ${id}`);
      setLoading(true);
      setError(null);
      
      const fetchedStudent = await getStudentById(id);
      if (!fetchedStudent) {
        console.error('❌ Student not found with ID:', id);
        setError(`Student with ID ${id} not found. The student may have been deleted or the ID is incorrect.`);
        setLoading(false);
        toast.error(`Student with ID ${id} not found`);
        return;
      }

      // Ensure all data is properly formatted to prevent runtime errors
      const processedStudent = {
        ...fetchedStudent,
        programs: Array.isArray(fetchedStudent.programs) ? fetchedStudent.programs.map(prog => ({
          ...prog,
          fee: typeof prog.fee === 'number' ? prog.fee : parseFloat(String(prog.fee || 0)),
          enrolledAt: prog.enrolledAt || new Date().toISOString(),
          completionDate: prog.completionDate || new Date().toISOString()
        })) : [],
        documents: Array.isArray(fetchedStudent.documents) ? fetchedStudent.documents.map(doc => ({
          ...doc,
          uploadDate: typeof doc.uploadDate === 'object' ? doc.uploadDate.toISOString() : String(doc.uploadDate || new Date().toISOString())
        })) : []
      };
      console.log('✅ Successfully fetched and processed student:', processedStudent.name);
      setStudent(processedStudent as Student);
      setError(null);
    } catch (err) {
      console.error('❌ Error fetching student details:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load student details';
      setError(errorMessage);
      toast.error('Error loading student details');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchId(studentId);
  }, [studentId, fetchId]);

  const handleDelete = async () => {
    if (!student) return;
    try {
      await deleteStudent(student.id);
      navigate('/students');
      toast.success('Student deleted successfully');
    } catch (error) {
      console.error('Error deleting student:', error);
      toast.error('Failed to delete student');
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  // Function to refresh student data after document operations
  const refreshStudentData = useCallback(async () => {
    await fetchId(studentId);
  }, [fetchId, studentId]);

  if (loading) {
    return <LoadingState />;
  }

  if (error || !student) {
    return <ErrorState message={error || 'Student not found'} />;
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-0">
      <StudentHeader student={student} onDelete={handleDelete} />
      
      <Tabs defaultValue="overview" value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="mb-4 sm:mb-6 w-full overflow-x-auto flex flex-nowrap sm:flex-wrap pb-0.5">
          <TabsTrigger value="overview" className="flex items-center text-[10px] sm:text-xs py-1 px-2 sm:text-sm sm:py-1.5 sm:px-3">
            <FileText size={12} className="mr-1 sm:mr-1.5" />
            <span className="whitespace-nowrap">Overview</span>
          </TabsTrigger>
          
          <TabsTrigger value="certifications" className="flex items-center text-[10px] sm:text-xs py-1 px-2 sm:text-sm sm:py-1.5 sm:px-3">
            <Badge size={12} className="mr-1 sm:mr-1.5" />
            <span className="whitespace-nowrap">Certifications</span>
          </TabsTrigger>
          
          <TabsTrigger value="enrollment-history" className="flex items-center text-[10px] sm:text-xs py-1 px-2 sm:text-sm sm:py-1.5 sm:px-3">
            <History size={12} className="mr-1 sm:mr-1.5" />
            <span className="whitespace-nowrap">History</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="lg:col-span-1">
              <StudentSummaryCard student={student} calculateTotalFees={calculateTotalFees} />
            </div>
            
            <div className="lg:col-span-2">
              <StudentPaymentSection student={student} />
              <ProgramsCard student={student} calculateTotalFees={calculateTotalFees} />
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="exams" className="mt-0">
          <ExamsTab studentId={student.id} programs={student.programs || []} />
        </TabsContent>
        
        <TabsContent value="certifications" className="mt-0">
          <CertificationsTab studentId={student.id} programs={student.programs || []} />
        </TabsContent>
        
        <TabsContent value="enrollment-history" className="mt-0">
          <EnrollmentHistoryTab studentId={student.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
